import React, { useCallback, useMemo, useState } from 'react';
import { message, Button, Tooltip } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import AdminActions from '@components/AdminActions';
import RoleChecker from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import Table, { ITableTabs } from '@components/Table';
import api from '@services/api';
import { formatCurrency } from '@utils/formatters';
import { useTranslation } from 'react-i18next';
import AddItemsToInvoice from '../../../InvoicesMot/AddItemsToInvoice';
import EditItemsOnInvoice from '../../../InvoicesMot/EditItemsOnInvoice';

interface IItemToMutate {
  id: number;
  description: string;
  quantity: number;
  price: number;
  totalValue: number;
  blItemEdit: string;
}

interface IItemData {
  idItemFatura: number;
  dsItem: string;
  nuQtdItem: number;
  nuValorUnitario: number;
  nuValorTotal: number;
  blItemEdit: number | null;
}

interface IInvoiceItems {
  invoiceId?: string | undefined;
  customerId?: string | undefined;
  canManageItems?: boolean;
  canAddNewItem?: boolean;
  changeItems?: Function;
  data: IItemData[];
  tabs?: ITableTabs;
}

const InvoiceItems: React.FC<IInvoiceItems> = ({
  customerId,
  invoiceId,
  canManageItems,
  canAddNewItem,
  changeItems,
  data,
  tabs,
}) => {
  const { t } = useTranslation('invoices');

  const [itemToEdit, setItemToEdit] = useState();
  const [showEditItem, setShowEditItem] = useState(false);
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);

  const deleteItem = useCallback(
    (itemId: string | number) =>
      api.delete(
        `/service-proxy/broker/${customerId}/invoices/${invoiceId}/items/${itemId}`,
      ),
    [customerId, invoiceId],
  );

  const formatItemPayload = useCallback(
    ({
      id,
      description,
      quantity,
      price,
      totalValue,
      blItemEdit,
    }: IItemToMutate) => ({
      idItemFatura: id,
      dsItem: `Extra - ${description}`,
      nuQtdItem: quantity,
      nuValorUnitario: price,
      nuValorTotal: totalValue,
      blItemEdit,
    }),
    [],
  );

  const handleMutateItems = useCallback(
    (payload: any, itemType?: string) => {
      let newListItems: any = data;
      if (itemType === 'CREATE') {
        newListItems = [
          ...data,
          ...payload.map((item: IItemToMutate) =>
            formatItemPayload({
              ...item,
              totalValue: item.price * item.quantity,
              blItemEdit: 'S',
            }),
          ),
        ];
        changeItems?.(newListItems);
      } else if (itemType === 'UPDATE') {
        newListItems = data?.map(item =>
          // @ts-ignore
          item?.idItemFatura === payload.id ? formatItemPayload(payload) : item,
        );
      } else {
        newListItems = data?.filter(item => item?.idItemFatura !== payload.id);
      }
      changeItems?.(newListItems);
    },
    [data, formatItemPayload, changeItems],
  );

  const handleActions = useCallback(
    async (actionType: string, record: any) => {
      const isUpdateUser = actionType === 'UPDATE';
      const itemId = record?.idItemFatura;

      if (isUpdateUser) {
        setItemToEdit(record);
        setShowEditItem(true);
      } else {
        try {
          await deleteItem(itemId);
          handleMutateItems({ id: itemId });
          message.success(t('invoiceItem.msgs.success-to-deleting'));
        } catch {
          message.error(t('invoiceItem.msgs.error-to-deleting'));
        }
      }
    },
    [t, deleteItem, handleMutateItems],
  );

  const itemsColumns = useMemo(
    () => [
      { title: t('invoiceItem.description'), dataIndex: 'dsItem' },
      {
        title: t('invoiceItem.quantity'),
        align: 'center' as const,
        dataIndex: 'nuQtdItem',
        render: (value: any) => value && Number(value).toFixed(0),
      },
      {
        title: t('invoiceItem.unitValue'),
        align: 'center' as const,
        dataIndex: 'nuValorUnitario',
        render: (value: any) => formatCurrency(Number(value)),
      },
      {
        title: t('invoiceItem.totalValue'),
        align: 'center' as const,
        dataIndex: 'nuValorTotal',
        render: (value: any) => formatCurrency(Number(value)),
      },
      ...(canManageItems
        ? [
            {
              title: t('invoiceItem.actions'),
              align: 'center' as const,
              dataIndex: 'actions',
              render: (
                value: any,
                record: {
                  blItemEdit: string;
                  idItemFatura: number;
                  dsItem: string;
                  status: string;
                },
              ) => {
                const canEdit =
                  record?.blItemEdit === 'S' && record?.idItemFatura;
                if (canEdit) {
                  return (
                    <AdminActions
                      alertDelete={t('items-modal.delete.alert')}
                      entityToExclude={record.dsItem}
                      ruleToNotExclude={record.status === 'SYNCHRONIZED'}
                      onEdit={() => handleActions('UPDATE', record)}
                      onDelete={() => handleActions('DELETE', record)}
                    />
                  );
                }
                return undefined;
              },
            },
          ]
        : []),
    ],
    [handleActions, t, canManageItems],
  );

  return (
    <>
      <Table
        title={t('cards.invoiceItems')}
        columns={itemsColumns}
        data={data}
        loading={!data}
        extraActions={
          canAddNewItem && (
            <RoleChecker role={rolesConstants.ADD_ITEMS_TO_INVOICE}>
              <Tooltip title={t('items-modal.new-items')}>
                <Button
                  icon={<PlusCircleOutlined />}
                  onClick={() => setShowAddItemsModal(true)}
                />
              </Tooltip>
            </RoleChecker>
          )
        }
        tableKey="invoiceItems"
        tabs={tabs}
        noPagination
      />

      {!canManageItems && (
        <>
          <RoleChecker role={rolesConstants.ADD_ITEMS_TO_INVOICE}>
            <AddItemsToInvoice
              visible={showAddItemsModal}
              onClose={() => setShowAddItemsModal(false)}
              invoiceId={invoiceId}
              customerId={customerId}
            />
          </RoleChecker>

          <EditItemsOnInvoice
            modalVisible={showEditItem}
            itemToEdit={itemToEdit}
            invoiceId={invoiceId}
            customerId={customerId}
            onClose={() => setShowEditItem(false)}
          />
        </>
      )}
    </>
  );
};

export default InvoiceItems;
